import {
  FC, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'react-tooltip';
import {
  Control, Controller, UseFormSetValue, UseFormWatch,
} from 'react-hook-form';

import useMaskedInput from 'hooks/useMaskedInput';
import { Mixpanel } from 'mixpanel';
import useGetMetric from 'hooks/useGetMetric';
import Button from 'components/Button';
import Input from 'components/Input';
import Select from 'components/Select';
import MaskInput from 'components/MaskedInput';
import Radio from 'components/Radio';
import useUser from 'api/hooks/useUser';
import { unMaskInput } from 'utils/helpers';
import { IFeasibilityCheck } from 'features/FeasibilityCheck';
import { cn } from 'utils/cn';

import { getChemicalFeasibility } from './utils/chemicalHelpers';

interface Props {
  setActiveTab: (value: number) => void;
  setValue: UseFormSetValue<IFeasibilityCheck>;
  formWatch: UseFormWatch<IFeasibilityCheck>;
  control: Control<IFeasibilityCheck>;
  isPDF?: boolean;
}

const ChemicalFeasibility: FC<Props> = ({
  setActiveTab,
  setValue,
  formWatch,
  control,
  isPDF,
}) => {
  const { t } = useTranslation();
  const { data: user } = useUser();
  const { ton } = useGetMetric();

  const applicationData = formWatch('application');
  const economicData = formWatch('economic');
  const plantSetUpData = formWatch('plant');

  const {
    expectedProductConsumption,
    dosageConcentration,
    startPh: formStartPh,
    targetPh: formTargetPh,
    stirredTank,
    aeration,
    reactionTime: formReactionTime,
    sulfate,
    chemicalFeasibility,
  } = formWatch('chemical');

  const [isExpProductConsumptionChanged, setIsExpProductConsumptionChanged] = useState(false);
  const {
    value: startPh,
    setValue: setStartPh,
    clean: cleanStartPh,
  } = useMaskedInput({
    defaultValue: '2',
    value: formStartPh,
    onChangeCallback: (value) => setValue('chemical.startPh', value),
  });
  const {
    value: targetPh,
    setValue: setTargetPh,
    clean: cleanTargetPh,
  } = useMaskedInput({
    defaultValue: '7',
    value: formTargetPh,
    onChangeCallback: (value) => setValue('chemical.targetPh', value),
  });
  const {
    value: reactionTime,
    setValue: setReactionTime,
    clean: cleanReactionTime,
  } = useMaskedInput({
    defaultValue: '15',
    value: formReactionTime,
    onChangeCallback: (value) => setValue('chemical.reactionTime', value),
  });

  const stirredTankOptions = [
    { value: t('stirredTank'),
      label: t('stirredTank') },
    { value: t('plugFlow'),
      label: t('plugFlow') },
  ];

  const aerationOptions = [
    { value: t('yes'),
      label: t('yes') },
    { value: t('no'),
      label: t('no_1') },
  ];

  const sulfateOptions = [
    { value: t('yes'),
      label: t('yes') },
    { value: t('no'),
      label: t('no_1') },
  ];

  useEffect(() => {
    if (!aeration?.value) {
      setValue('chemical.aeration', aerationOptions[1]);
    }

    if (!sulfate?.value) {
      setValue('chemical.sulfate', sulfateOptions[1]);
    }

    if (!stirredTank) {
      setValue('chemical.stirredTank', stirredTankOptions[0].value);
    }
  }, []);
  const handleNext = (nextStep: number) => () => {
    setActiveTab(nextStep);
    Mixpanel.track('Feasibility Check Step 4', {
      role: user?.role?.name,
      organization: user?.organization,
    });
  };

  const errorExpectedConsumptionHandle = useMemo(() => {
    const value = expectedProductConsumption?.replace(/[^0-9]/g, '');

    if (Number(value) < 1) {
      return t('minimumPossibleValue') + ' is 1';
    }

    if (Number(value) > 1000000) {
      return t('maximumPossibleValue') + ' is 1\'000\'000';
    }

    return '';
  }, [expectedProductConsumption]);

  const errorReactionTimeHandle = useMemo(() => {
    if (Number(cleanReactionTime) < 0.01) {
      return t('minimumPossibleValue') + ' is 0,01';
    }

    if (Number(cleanReactionTime) > 1000000) {
      return t('maximumPossibleValue') + ' is 1\'000\'000';
    }

    return '';
  }, [reactionTime]);

  const errorStartpHHandle = useMemo(() => {
    if (Number(cleanStartPh) < -2) {
      return t('minimumPossibleValue') + ' is -2';
    }

    if (Number(cleanStartPh) > 14) {
      return t('maximumPossibleValue') + ' is 14';
    }

    return '';
  }, [startPh]);

  const errorTargetPhHandle = useMemo(() => {
    if (Number(cleanTargetPh) < -2) {
      return t('minimumPossibleValue') + ' is -2';
    }

    if (Number(cleanTargetPh) > 14) {
      return t('maximumPossibleValue') + ' is 14';
    }

    return '';
  }, [targetPh]);

  useEffect(() => {
    const result =
      ((Number(unMaskInput(expectedProductConsumption)) * Number(economicData?.concentrationOmya?.value)) / 100 /
        365 / Number(plantSetUpData?.averageInflow)) * 1000000;

    setValue('chemical.dosageConcentration', Math.round(result));
  }, [
    expectedProductConsumption,
    economicData?.concentrationOmya?.value,
    plantSetUpData?.averageInflow,
  ]);

  useEffect(() => {
    let result = t('no_1');

    if (aeration?.value
        && economicData?.omya?.value
        && reactionTime
        && stirredTank
        && targetPh) {
      if (getChemicalFeasibility({
        targetPh: Number(cleanTargetPh),
        stirredTank: stirredTank,
        aeration: aeration.value,
        reactionTime: Number(cleanReactionTime),
        product: economicData?.omya?.value,
        t,
      })) {
        result = t('yes');
      }
    }

    setValue('chemical.chemicalFeasibility', result);
  }, [aeration?.value, economicData?.omya?.value, reactionTime, stirredTank, targetPh]);

  const disabledState = useMemo(() => (
    !economicData?.omya?.value ||
      !economicData?.concentrationOmya?.value ||
      !expectedProductConsumption ||
      !dosageConcentration ||
      !startPh ||
      !targetPh ||
      !aeration?.value ||
      !reactionTime
  ), [
    aeration?.value,
    dosageConcentration,
    economicData?.concentrationOmya?.value,
    economicData?.omya?.value,
    expectedProductConsumption,
    reactionTime,
    startPh,
    targetPh,
  ]);

  useEffect(() => {
    setValue('chemical.expectedProductConsumption',
      `${economicData?.expectedProductConsumption || 1}`,
    );
  }, [economicData?.expectedProductConsumption]);

  return (
    <form className={cn(
      'border border-primitiveGrey p-8 rounded-2xl',
      { 'md:py-6 md:px-4': !isPDF },
    )}>
      <div className="text-black text-xl font-bold mb-8">
        {t('chemicalFeasibility')}
      </div>
      <div className="text-black text-lg font-bold mb-6">
        {t('productReactivityCheck')}
      </div>
      <div className={cn(
        'grid gap-6 grid-cols-2 grid-rows-2 ',
        { 'md:grid-cols-1 md:gap-2': !isPDF },
      )}>
        <div
          className={cn(
            'w-full mb-1 break-inside-avoid',
            { 'md:mb-4': !isPDF },
          )}
          data-tooltip-id="product-input"
        >
          <Select
            disabled={true}
            value={economicData?.omya ? [economicData.omya] : []}
            options={economicData?.omya ? [economicData.omya]: []}
            placeholder={t('specify')}
            title={t('product')}
            onChange={() => {}}
          />
        </div>
        <Tooltip
          id="product-input"
          place="bottom-start"
          className='-mt-6'
          variant="light"
          noArrow
        >
          {t('updateProductText')}
        </Tooltip>
        <div
          className={cn(
            'w-full mb-1 break-inside-avoid',
            { 'md:mb-4': !isPDF },
          )}
          data-tooltip-id="concentration-input"
        >
          <Input
            disabled
            value={`${economicData?.concentrationOmya?.value || ''}`}
            className="w-full"
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={`${t('concentration')} (%)`}
          />
        </div>
        <Tooltip
          id="concentration-input"
          place="bottom-start"
          className="-mt-6"
          variant="light"
          noArrow
        >
          {t('updateConcentrationText')}
        </Tooltip>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <MaskInput
            value={`${expectedProductConsumption}`}
            className="w-full"
            titleClassName="top-[-9px] left-[8px] bg-blue-gradient"
            title={`${t('expectedAnnualConsumption')} ${ton}`}
            onChange={(value) => {
              if (Number(value) !== economicData?.expectedProductConsumption) {
                setIsExpProductConsumptionChanged(true);
              } else {
                setIsExpProductConsumptionChanged(false);
              }

              setValue('chemical.expectedProductConsumption', value);
            }}
            isBlueText={isExpProductConsumptionChanged}
            isGreyText={!isExpProductConsumptionChanged}
            isBlueBg={true}
            redBorder={errorExpectedConsumptionHandle.length > 0}
          />
          <div className="text-[#FF3B30] text-xs mt-0 h-2">
            {errorExpectedConsumptionHandle.length > 0 && (
              <div> {errorExpectedConsumptionHandle}</div>
            )}
          </div>
        </div>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <MaskInput
            disabled
            value={`${dosageConcentration || ''}`}
            className="w-full"
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={`${t('dosageConcentration')} (mg/L)`}
            isGreyText
          />
        </div>
        <div className="w-full col-divider h-px bg-[#E2E2E2] mb-1"/>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <MaskInput
            value={startPh}
            className="w-full"
            titleClassName="top-[-9px] left-[8px]"
            title={t('startpH')}
            onChange={setStartPh}
            redBorder={errorStartpHHandle.length > 0}
            decimalLimit={1}
          />
          <div className="text-[#FF3B30] text-xs mt-0 h-2">
            {errorStartpHHandle.length > 0 && <div> {errorStartpHHandle}</div>}
          </div>
        </div>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <MaskInput
            value={targetPh}
            className="w-full"
            titleClassName="top-[-9px] left-[8px]"
            title={t('targetpH')}
            onChange={setTargetPh}
            decimalLimit={1}
            redBorder={errorTargetPhHandle.length > 0}
          />
          <div className="text-[#FF3B30] text-xs ml-1 mt-1 h-2">
            {errorTargetPhHandle.length > 0 && (
              <div> {errorTargetPhHandle}</div>
            )}
          </div>
        </div>
        <div className="w-full col-divider h-px bg-[#E2E2E2] mb-1"/>
        <div className={cn(
          'w-full col-span-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <div className="text-black font-bold mb-3">{t('dosingPoint')}</div>
          <div className="flex">
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <>
                  {stirredTankOptions?.map((option, i) => (
                    <div key={option.value + i} className="radio mr-20">
                      <Radio
                        className="mr-2 w-5 h-5"
                        value={option.value}
                        checked={option.value === value}
                        onChange={(e) => {
                          onChange(e.target.value);
                        }}
                      />
                      {option.value}
                    </div>))}
                </>
              )}
              name="chemical.stirredTank"
            />
          </div>
        </div>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <Controller
            control={control}
            rules={{
              required: true,
            }}
            render={({ field: { onChange, value } }) => (
              <Select
                value={value ? [value] : []}
                options={aerationOptions}
                placeholder={t('specify')}
                title={t('aeration')}
                onChange={onChange}
              />
            )}
            name="chemical.aeration"
          />
        </div>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <MaskInput
            value={reactionTime}
            className="w-full"
            titleClassName="top-[-9px] left-[8px]"
            title={`${t('reactionTime')} (min)`}
            onChange={setReactionTime}
            redBorder={errorReactionTimeHandle.length > 0}
            decimalLimit={2}
          />
          <div className="text-[#FF3B30] text-xs mt-0 h-2">
            {errorReactionTimeHandle.length > 0 && (
              <div> {errorReactionTimeHandle}</div>
            )}
          </div>
        </div>
        <div className={cn(
          'w-full mb-1 break-inside-avoid',
          { 'md:mb-4': !isPDF },
        )}>
          <Input
            disabled
            value={chemicalFeasibility}
            className="w-full"
            titleClassName="top-[-9px] left-[8px] bg-disable-gradient"
            title={t('reactivityIsSufficient')}
            isGreyText
          />
        </div>
        {applicationData?.industry?.value?.includes(t('metal')) && (
          <div className={cn(
            'w-full mb-1 break-inside-avoid',
            { 'md:mb-4': !isPDF },
          )}>
            <Controller
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange, value } }) => (
                <Select
                  value={value ? [value] : []}
                  options={sulfateOptions}
                  placeholder={t('specify')}
                  title={`${t('isSulfate')} > 1000 mg/L`}
                  onChange={onChange}
                />
              )}
              name="chemical.sulfate"
            />
          </div>
        )}
      </div>
      {!isPDF && (
        <div className="mt-8 flex justify-center md:mt-6">
          <Button
            onClick={() => setActiveTab(3)}
            className="mr-4"
            btnType="secondary"
          >
            {t('back')}
          </Button>
          <Button onClick={handleNext(6)} className="mr-4" btnType="secondary">
            {t('skipToBrief')}
          </Button>
          <Button disabled={disabledState} onClick={handleNext(5)}>
            {t('next')}
          </Button>
        </div>
      )}
    </form>
  );
};

export default ChemicalFeasibility;
